import { Box, Button, Skeleton, Stack, Typography } from '@mui/material'
import { useMemo } from 'react'
import {
  Divider,
  IconButton,
  Icons,
  PointDisplay,
  SkeletonText,
} from 'utoniq-ui'

import {
  Collection,
  CollectionFeeDetailFragment,
  Event,
  FeeDetailFragment,
  ItemType,
  Maybe,
  PaymentDetailFragment,
  PersonalInformationType,
  PrepaidPointBalance,
  UserAddressFragment,
  UserCartStoreItemConfirmationEventCollectionFragment,
  UserCartStoreItemPurchaseConfirmationFragment,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import { StorageImage, useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
export * from './fragments'
import { SxProps } from '@mui/system'

import { ContentFrame } from '@/components/layouts/ContentFrame'
import {
  ExpiredCardModal,
  PaymentErrorModal,
  Require3DSecureModal,
} from '@/components/organisms/functionals/PaymentErrorModal'
import {
  OrderDetailCell,
  TotalPaymentCell,
  TotalPaymentCellProps,
} from '@/components/pages/StoreItemPurchaseProceed/template'
import { generateCreditCardLogoPath } from '@/core/creditCard'
import { individualDisplayResponsive } from '@/core/individual'

export type State = {
  cart: Maybe<UserCartStoreItemPurchaseConfirmationFragment> | undefined
  cartItems: UserCartStoreItemPurchaseConfirmationFragment['items']
  cartItemsByCollection:
    | {
        collection: Maybe<Pick<Collection, 'id' | 'title'>> | undefined
        items: UserCartStoreItemPurchaseConfirmationFragment['items']
        eventCollection:
          | UserCartStoreItemConfirmationEventCollectionFragment
          | undefined
      }[]
    | null
  fullName: string
  paymentSource: Maybe<PaymentDetailFragment> | undefined
  address: Maybe<UserAddressFragment['address']>
  phoneNumber: string
  isPhoneNumberVerified: boolean
  isCardRegistered: boolean
  loading: boolean
  receiveTime?: boolean
  submitting: boolean
  systemFee: Maybe<CollectionFeeDetailFragment>[]
  shippingFee: Maybe<FeeDetailFragment>
  konbiniFee: Maybe<FeeDetailFragment>
  purchaseType?: 'cash' | 'konbini' | 'prepaid_point'
  sendingVerification: boolean
  prepaidPointBalance:
    | Maybe<
        Pick<
          PrepaidPointBalance,
          | 'balance'
          | 'prepaidPointId'
          | 'formattedPrepaidPointBarance'
          | 'lastUpdatedAt'
        >
      >
    | undefined
  pickupTimes:
    | {
        collectionId: string
        pickupTime: string | null
      }[]
    | null
  events: (Maybe<Pick<Event, 'id' | 'title' | 'note'>> | undefined)[]
  openExpiredCardModal: boolean
  openPaymentErrorModal: boolean
  openRequire3DSecureModal: boolean
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onCheckout: () => Promise<void>
  onSendVerificationSMS: () => Promise<void>
  onCloseRequire3DSecureModal: () => void
  onCloseExpiredCardModal: () => void
  onClosePaymentErrorModal: () => void
}

export const StoreItemPurchaseConfirmationTemplate: Template<State, Action> = ({
  state,
  onCheckout,
  onSendVerificationSMS,
  onCloseRequire3DSecureModal,
  onCloseExpiredCardModal,
  onClosePaymentErrorModal,
}) => {
  const { t } = useTranslation('storeItemPurchaseConfirmation')
  const { routerNames, routerPages, generatePath, router } =
    useRouter<{ cartId: string }>()

  const hasMerchItem = useMemo<boolean>(() => {
    if (!state.cartItems) return false
    return state.cartItems.some(item => item.item.type === ItemType.Merch)
  }, [state.cartItems])

  // 個人情報の登録必須項目
  const personalInformationRequirements = useMemo<string[]>(() => {
    if (!state.cartItems) return []

    const result = state.cartItems.reduce((acc: string[], item) => {
      const requirements = item.item.personalInformationRequirements

      return Array.isArray(requirements) ? acc.concat(requirements) : acc
    }, [])

    return [...new Set(result)]
  }, [state.cartItems])

  // 入力項目が不足し、次のステップに進めない状態
  const disableToNext = useMemo<boolean>(
    () =>
      // 氏名未登録
      (personalInformationRequirements.includes(
        PersonalInformationType.FullName
      ) &&
        !state.fullName) ||
      // 電番未認証
      (personalInformationRequirements.includes(
        PersonalInformationType.PhoneNumber
      ) &&
        !state.isPhoneNumberVerified) ||
      // 住所未登録
      (personalInformationRequirements.includes(
        PersonalInformationType.Address
      ) &&
        !state.address?.fullAddress) ||
      // ポイント不足
      (state.cart?.paymentType === 'prepaid_point' &&
        state.cart.totalPrice > (state.prepaidPointBalance?.balance ?? 0)) ||
      // 氏名未入力
      (state.purchaseType === 'konbini' && !state.fullName) ||
      // クレカ未登録
      (state.purchaseType === 'cash' && !state.paymentSource?.id),
    [
      state.fullName,
      personalInformationRequirements,
      state.cart,
      state.purchaseType,
      state.address,
      state.isPhoneNumberVerified,
      state.prepaidPointBalance?.balance,
      state.paymentSource?.id,
    ]
  )

  const isReceiveTime = useMemo(() => {
    return state.cartItemsByCollection?.some(
      ({ eventCollection }) =>
        state.pickupTimes?.find(
          ({ collectionId }) => collectionId === eventCollection?.collectionId
        )?.pickupTime
    )
  }, [state.cartItemsByCollection, state.pickupTimes])

  const displayResponsive = individualDisplayResponsive()

  return (
    <BaseLayout
      header={{
        left: (
          <IconButton.Back
            edge="start"
            size="medium"
            onClick={() =>
              router.replace(
                state.cart?.id
                  ? generatePath(routerPages.StoreItemPurchaseProceed, {
                      cartId: state.cart?.id ?? '#',
                    })
                  : routerPages.StoreCartList
              )
            }
          />
        ),
        title: t('title'),
        ...(displayResponsive
          ? {
              responsive: true,
              maxWidth: 1120,
            }
          : {}),
      }}
      {...(displayResponsive
        ? {
            responsive: true,
            maxWidth: 1120,
          }
        : {})}
    >
      <Head title={routerNames.StoreItemPurchaseConfirmation} />
      <BillingDetailCell
        purchaseType={state.purchaseType}
        systemFee={state.systemFee}
        shippingFee={state.shippingFee}
        konbiniFee={state.konbiniFee}
        totalPrice={state.cart?.totalPrice}
        formattedTotalPrice={state.cart?.formattedTotalPrice}
        paymentType={state.cart?.paymentType}
      />
      {!!personalInformationRequirements.length && (
        <ShippingAddressCell
          loading={state.loading}
          fullName={state.fullName}
          address={state.address}
          isPhoneNumberVerified={state.isPhoneNumberVerified}
          phoneNumber={state.phoneNumber}
          onSendVerificationSMS={onSendVerificationSMS}
          personalInformationRequirements={personalInformationRequirements}
        />
      )}
      {state.loading ? (
        <PaymentInfoCellSkeleton />
      ) : (
        <PaymentInfoCell
          formattedPrepaidPointBalance={
            state.prepaidPointBalance?.formattedPrepaidPointBarance ?? 0
          }
          paymentSource={state.paymentSource}
          purchaseType={state.purchaseType}
        />
      )}
      <OrderDetailCell
        cartItemsByCollection={state.cartItemsByCollection}
        pickupTimes={state.pickupTimes}
        loading={state.loading}
        purchaseType={state.purchaseType}
        systemFee={state.systemFee}
        shippingFee={state.shippingFee}
        konbiniFee={state.konbiniFee}
        totalPrice={state.cart?.totalPrice}
        formattedTotalPrice={state.cart?.formattedTotalPrice}
        paymentType={state.cart?.paymentType}
        hiddenPaymentInfo
        purchaseLimitPerPickupErrorIds={[]}
        onRemovePurchaseLimitPerPickupErrorIds={() => {}}
        responsive={displayResponsive}
      />
      <ButtonConfirmOrderCell
        loading={state.loading}
        submitting={state.submitting}
        onClick={onCheckout}
        disabled={disableToNext}
        isReceiveTime={isReceiveTime}
        sx={{ pt: 0 }}
        events={state.events}
        cartId={state.cart?.id}
      />

      <Require3DSecureModal
        open={state.openRequire3DSecureModal}
        onClose={onCloseRequire3DSecureModal}
      />

      <ExpiredCardModal
        open={state.openExpiredCardModal}
        onClose={onCloseExpiredCardModal}
      />

      <PaymentErrorModal
        open={state.openPaymentErrorModal}
        onClose={onClosePaymentErrorModal}
      />
    </BaseLayout>
  )
}

type BillingDetailCellProps = TotalPaymentCellProps & {}
const BillingDetailCell: Cell<BillingDetailCellProps> = props => {
  const { t } = useTranslation('storeItemPurchaseConfirmation')
  return (
    <CellFrame sx={{ pb: 4 }}>
      <Stack sx={{ bgcolor: 'grey.50', px: 5, py: 2 }}>
        <ContentFrame>
          <Typography variant="h5" color="text.secondary">
            {t('billingDetail', 'ご請求内容')}
          </Typography>
        </ContentFrame>
      </Stack>
      <ContentFrame>
        <Stack sx={{ p: 5 }}>
          <TotalPaymentCell {...props} />
        </Stack>
      </ContentFrame>
    </CellFrame>
  )
}

type ShippingAddressCellProps = {
  fullName: State['fullName']
  address: State['address']
  isPhoneNumberVerified: State['isPhoneNumberVerified']
  phoneNumber: State['phoneNumber']
  loading: boolean
  onSendVerificationSMS: Action['onSendVerificationSMS']
  personalInformationRequirements: string[]
}
export const ShippingAddressCell: Cell<ShippingAddressCellProps> = ({
  fullName,
  address,
  isPhoneNumberVerified,
  phoneNumber,
  loading,
  onSendVerificationSMS,
  personalInformationRequirements,
}) => {
  const { t } = useTranslation('storeItemPurchaseConfirmation')
  const hasError = useMemo<boolean>(
    () =>
      // 氏名未登録
      (personalInformationRequirements.includes(
        PersonalInformationType.FullName
      ) &&
        !fullName) ||
      // 電番未認証
      (personalInformationRequirements.includes(
        PersonalInformationType.PhoneNumber
      ) &&
        !isPhoneNumberVerified) ||
      // 住所未登録
      (personalInformationRequirements.includes(
        PersonalInformationType.Address
      ) &&
        !address?.fullAddress),
    [fullName, personalInformationRequirements, address, isPhoneNumberVerified]
  )

  if (loading) {
    return <ShippingAddressCellSkeleton />
  }

  return (
    <CellFrame sx={{ pb: 4 }}>
      <Stack sx={{ bgcolor: 'grey.50', px: 5, py: 2 }}>
        <ContentFrame>
          <Typography variant="h5" color="text.secondary">
            {t('personalInfo', 'お客様情報')}
          </Typography>
        </ContentFrame>
      </Stack>
      <ContentFrame>
        <Stack sx={{ px: 5, py: 3 }}>
          {hasError && (
            <Typography variant="body2" color="error.main">
              {t(
                'personalInfoError',
                '商品の購入にはお客様情報の登録が必要です。'
              )}
            </Typography>
          )}
          <Stack spacing={1} sx={{ pt: hasError ? 2 : 0 }}>
            <Typography variant="subtitle1" color="text.secondary">
              {t('familyName', '氏名')}
            </Typography>
            <Typography
              variant="body2"
              color={fullName ? 'text.primary' : 'text.hint'}
            >
              {fullName ??
                t('unregisteredFullName', '氏名が登録されていません')}
            </Typography>
          </Stack>
          {personalInformationRequirements.includes(
            PersonalInformationType.Address
          ) && (
            <Stack spacing={1} sx={{ pt: 2 }}>
              <Typography variant="subtitle1" color="text.secondary">
                {t('address')}
              </Typography>
              <Typography
                variant="body2"
                color={address?.fullAddress ? 'text.primary' : 'text.hint'}
              >
                {address?.fullAddress ?? t('unregisteredAddress')}
              </Typography>
            </Stack>
          )}
          <Stack spacing={1} sx={{ pt: 2 }}>
            <Stack direction="row" spacing={3} alignItems="center">
              <Typography variant="subtitle1" color="text.secondary">
                {t('phoneNumber')}
              </Typography>
              {!isPhoneNumberVerified ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Icons.Error
                    fontSize="small"
                    sx={{ color: 'error.main', mt: -1 }}
                  />
                  <Typography variant="body2" color="error.main">
                    {t('unverify')}
                  </Typography>
                  {phoneNumber && (
                    <Button
                      variant="text"
                      color="primary"
                      loading={loading}
                      onClick={onSendVerificationSMS}
                    >
                      {t('verify')}
                    </Button>
                  )}
                </Stack>
              ) : (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Icons.Success
                    fontSize="small"
                    sx={{ color: 'text.hint', mt: -1 }}
                  />
                  <Typography variant="body2" color="text.hint">
                    {t('verified')}
                  </Typography>
                </Stack>
              )}
            </Stack>
            <Typography variant="body2">{phoneNumber}</Typography>
          </Stack>
        </Stack>
      </ContentFrame>
    </CellFrame>
  )
}

type PaymentInfoCellProps = {
  formattedPrepaidPointBalance: string | number
  paymentSource?: Maybe<PaymentDetailFragment> | undefined
  purchaseType?: string
}

export const PaymentInfoCell: Cell<PaymentInfoCellProps> = ({
  formattedPrepaidPointBalance,
  paymentSource,
  purchaseType,
}) => {
  const { t } = useTranslation('storeItemPurchaseConfirmation')

  switch (purchaseType) {
    case 'konbini':
      return (
        <CellFrame sx={{ pb: 4 }}>
          <Stack sx={{ bgcolor: 'grey.50', px: 5, py: 2 }}>
            <ContentFrame>
              <Typography variant="h5" color="text.secondary">
                {t('paymentInfo', '支払い情報')}
              </Typography>
            </ContentFrame>
          </Stack>
          <ContentFrame>
            <Stack sx={{ p: 5 }}>
              <Typography variant="h5">
                {t('convenienceStore', 'コンビニ決済')}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mt: 2 }}>
                {t(
                  'convenienceStoreDesc',
                  'ファミリーマート、ローソン、ミニストップ、セイコーマートに対応しています。'
                )}
              </Typography>
              <Typography variant="caption" sx={{ pt: 1 }} color="grey.700">
                {t('storeItemNote')}
              </Typography>
            </Stack>
          </ContentFrame>
        </CellFrame>
      )
    case 'cash':
      return (
        <CellFrame sx={{ pb: 4 }}>
          <Stack sx={{ bgcolor: 'grey.50', px: 5, py: 2 }}>
            <ContentFrame>
              <Typography variant="h5" color="text.secondary">
                {t('paymentInfo', '支払い情報')}
              </Typography>
            </ContentFrame>
          </Stack>
          <ContentFrame>
            <Stack sx={{ p: 5 }}>
              <Stack direction="row" spacing={3} alignItems="center">
                <StorageImage
                  height={25}
                  src={generateCreditCardLogoPath(paymentSource?.brand)}
                  target="common"
                />
                <Typography variant="h6">
                  {t('last4Digits', { number: paymentSource?.last4 })}
                </Typography>
              </Stack>
            </Stack>
          </ContentFrame>
        </CellFrame>
      )
    case 'prepaid_point':
      return (
        <CellFrame sx={{ pb: 4 }} color="text.secondary">
          <Stack sx={{ bgcolor: 'grey.50', px: 5, py: 2 }}>
            <ContentFrame>
              <Typography variant="h5" color="text.secondary">
                {t('paymentInfo', '支払い情報')}
              </Typography>
            </ContentFrame>
          </Stack>
          <ContentFrame>
            <Stack sx={{ p: 5 }}>
              <Stack direction="row" mt={1} justifyContent="space-between">
                <Stack spacing={1}>
                  <Typography variant="h5">
                    {t('purchaseByPoint', 'ポイントで購入')}
                  </Typography>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Typography variant="subtitle1" color="text.secondary">
                      {t('pointBalance', 'ポイント残高')}
                    </Typography>
                    <PointDisplay point={formattedPrepaidPointBalance} />
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
          </ContentFrame>
        </CellFrame>
      )
    default:
      return null
  }
}

type ButtonConfirmOrderCellProps = {
  loading: boolean
  submitting: boolean
  onClick: () => void
  disabled: boolean
  sx?: SxProps
  isReceiveTime?: boolean
  events: State['events']
  cartId?: string
}
const ButtonConfirmOrderCell: Cell<ButtonConfirmOrderCellProps> = ({
  submitting,
  loading,
  sx,
  isReceiveTime,
  events,
  cartId,
  ...props
}) => {
  const { t } = useTranslation('storeItemPurchaseConfirmation')
  const { router, generatePath, routerPages } = useRouter()

  if (loading) {
    return (
      <Skeleton
        variant="rectangular"
        sx={{ width: 244, height: 44, borderRadius: 34, mx: 'auto' }}
      />
    )
  }

  return (
    <CellFrame sx={{ pb: 4 }} color="text.secondary">
      {!!events.length && (
        <Stack sx={{ bgcolor: 'grey.50', px: 5, py: 2 }}>
          <ContentFrame>
            <Typography variant="h5" color="text.secondary">
              {t('notes', '注意事項')}
            </Typography>
          </ContentFrame>
        </Stack>
      )}

      <ContentFrame>
        <Stack sx={{ p: 5 }}>
          {events.map(event => {
            return (
              <Stack key={event?.id} spacing={1} sx={{ pb: 5 }}>
                <Typography variant="subtitle1" color="text.secondary">
                  {t('notesTitle', { title: event?.title })}
                </Typography>
                <Typography color="text.secondary" variant="caption">
                  {event?.note}
                </Typography>
              </Stack>
            )
          })}
          <Stack alignItems="center" sx={{ pt: 4 }}>
            {!!events.length && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ pb: 2 }}
              >
                {t('agreeNote', '上記の注意事項に同意して')}
              </Typography>
            )}
            <Button
              size="large"
              sx={{ width: 212 }}
              loading={submitting}
              {...props}
            >
              {t('confirmOrder', '注文を確定する')}
            </Button>
            <Button
              onClick={() =>
                router.goBack(
                  cartId
                    ? generatePath(routerPages.StoreItemPurchaseProceed, {
                        cartId,
                      })
                    : routerPages.StoreCartList
                )
              }
              size="large"
              variant="text"
              sx={{ width: 212, mt: 2 }}
            >
              {t('close', '戻る')}
            </Button>
          </Stack>
        </Stack>
      </ContentFrame>
    </CellFrame>
  )
}

const ShippingAddressCellSkeleton: Cell = () => (
  <CellFrame sx={{ pb: 5, mt: 5 }}>
    <Box sx={{ px: 5, py: 2, bgcolor: 'grey.50' }}>
      <SkeletonText variant="h3" width="50%" />
    </Box>
    <ContentFrame>
      <Stack
        direction="row"
        spacing={3}
        alignItems="center"
        sx={{ px: 5, py: 3 }}
      >
        <Skeleton variant="circular" sx={{ width: 40, height: 40 }} />
        <Stack flex={1}>
          <SkeletonText variant="small" width="75%" />
          <Stack sx={{ pt: 2 }}>
            <SkeletonText variant="small" width="85%" />
            <SkeletonText variant="small" width="85%" />
          </Stack>
        </Stack>
      </Stack>
    </ContentFrame>
  </CellFrame>
)

const PaymentInfoCellSkeleton: Cell = () => {
  return (
    <CellFrame sx={{ pb: 4 }}>
      <Stack sx={{ px: 5, py: 2, bgcolor: 'grey.50' }}>
        <SkeletonText variant="h3" width="50%" />
      </Stack>
      <ContentFrame>
        <Stack sx={{ px: 5, py: 3 }} spacing={7}>
          <SkeletonText sx={{ mt: 4 }} variant="large" width="100%" />
          <Divider />
          <SkeletonText sx={{ mt: 4 }} variant="large" width="100%" />
        </Stack>
      </ContentFrame>
    </CellFrame>
  )
}
